import React, { useState } from "react";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import { useCarriersListDetails, useDeleteCarrier } from "hooks/useCarrier";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import { CarrierDetailModel } from "app/data/carriers/models";
import "./carriersaddEditDetails.scss";
import { Card } from "ui-components/card/card";
import Loading from "ui-components/loading/loading";
import { UserUtils } from "app/data/user/userUtils";
import UserState from "slices/user/UserState";
import { userSelector } from "slices/user/userSlice";
import { ReactComponent as EditIcon } from "images/edit_solid.svg";
import { useSelector } from "react-redux";
import Button, { ButtonThemes } from "ui-components/button/button";
import Tag from "ui-components/molecules/tag/tag";
import { TagColor } from "app/data/common/tagColor";
import CarriersAddEditDetails from "./carriersaddEditDetails";
import { toast } from "react-toastify";
import { ReactComponent as AlertIcon } from "images/alert.svg";
import ConfirmationModal from "ui-components/confirmation-modal/confirmationModal";
import { Popover } from "ui-components/popover/popover";

interface CarrierInfo {
    carrierID: string;
    carrierName: string;
    status: string;
    phone: string;
    email: string;
    streetAddress: string;
    city: string;
    stateProvince: string;
    zipPostalCode: string;
    country: string;
    scacCode: string;
    motorCarrierNo: string;
    generalLiabilityDate: string;
    autoLiabilityDate: string;
    motorCargoDate: string;
    workmanCompDate: string;
    notes: string;
}

interface CarrierViewDetailsProps {
    show: boolean;
    onClose: () => void;
    carrierNumber?: number;
    onDataUpdated?: () => void;
}

const mapCarrierToFormInfo = (carrier: CarrierDetailModel): CarrierInfo => ({
    carrierID: carrier.carrierNumber?.toString() || "(Auto-generated)",
    carrierName: carrier.carrierName || "",
    status: carrier.status || "",
    phone: carrier.phoneNumber || "",
    email: carrier.carrierEmail || "",
    streetAddress: carrier.carrierAddress || "",
    city: carrier.city || "",
    stateProvince: carrier.stateProvince || "",
    zipPostalCode: carrier.zipPostalCode || "",
    country: carrier.country || "",
    scacCode: carrier.scacCode || "",
    motorCarrierNo: carrier.mcNumber || "",
    generalLiabilityDate: carrier.generalLiability?.expiryDate || "",
    autoLiabilityDate: carrier.autoLiability?.expiryDate || "",
    motorCargoDate: carrier.motorCargo?.expiryDate || "",
    workmanCompDate: carrier.workmanComp?.expiryDate || "",
    notes: carrier.notes || "",
});

const CarrierViewDetails: React.FC<CarrierViewDetailsProps> = ({ show, onClose, carrierNumber, onDataUpdated }) => {
    const userState: UserState = useSelector(userSelector);
    const { data, isLoading, error, refetch } = useCarriersListDetails(carrierNumber);
    const [isEditing, setIsEditing] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    const { mutateAsync: deleteCarrier } = useDeleteCarrier();

    if (!carrierNumber) return null;

    const carrier: CarrierDetailModel | undefined = data?.data;

    const renderInfoField = (label: string | React.ReactNode, value: string | number | boolean | undefined | null | React.ReactNode, isValid?: string) => (
        <div className="info-field">
            <div className="info-field__content-wrapper">
                <span className="info-field__label">{label}</span>
                <span className="info-field__value">{value ? value : "N/A"}</span>
            </div>
            {isValid !== undefined && (
                <Tag mods={{ color: isValid === "VALID" ? TagColor.GREEN : TagColor.ORANGE, uppercase: true }}>
                    {isValid}
                </Tag>
            )}
        </div>
    );

    const renderSimpleCardContent = (content: string | JSX.Element | null) => (
        <div className="simple-card-content">
            {content}
        </div>
    );

    const handleEditClick = () => {
        if (carrier) {
            setIsEditing(true);
        }
    };

    const handleEditClose = () => {
        setIsEditing(false);
        refetch();
        if (onDataUpdated) onDataUpdated();
    };

    const handleOpenDeleteConfirmation = () => {
        if (carrierNumber) {
            setShowDeleteConfirmation(true);
        }
    };

    const handleConfirmDelete = async () => {
        if (!carrierNumber) return;

        setShowDeleteConfirmation(false);

        try {
            await deleteCarrier(carrierNumber);
            toast.info(`Carrier ${carrier?.carrierName || carrierNumber} deleted successfully!`);
            onClose();
            if (onDataUpdated) onDataUpdated();
        } catch (err) {
            toast.error("Failed to delete carrier");
        }
    };


    if (isEditing && carrier) {
        const initialCarrierData = mapCarrierToFormInfo(carrier);
        return (
            <CarriersAddEditDetails
                show={show}
                onClose={handleEditClose}
                initialCarrierData={initialCarrierData}
            />
        );
    }

    return (
        <>
            <SlideOutSidebar
                header={carrier?.carrierName || "Carrier Details"}
                onClose={onClose}
                show={show}
                size="small"
            >
                {isLoading && <div className="loading"><Loading isLoading={true} /></div>}

                {error && <XGSErrorMessage>{(error as any)?.message || "Error loading carrier details"}</XGSErrorMessage>}

                {carrier && (
                    <div className="carrier-view-container">
                        <div className="profile-header-carrier__title">
                            <span className="profile-header-carrier__title-text">{"Carrier Information"}</span>
                            {UserUtils.isXGSAdministrator(userState.profile) && (
                                <div className="edit" onClick={handleEditClick}>
                                    <EditIcon />
                                    <span>Edit</span>
                                </div>
                            )}
                        </div>

                        <Card hideButtons={true} mix="carrier-info-card">
                            {renderInfoField("Carrier ID (Auto-generated)", carrier?.carrierNumber)}
                            {renderInfoField("Status", <Tag mods={{ color: carrier?.status === "ACTIVE" ? TagColor.GREEN : TagColor.GREY, uppercase: true }}>{carrier?.status}</Tag>)}
                            {renderInfoField("Carrier Name", carrier?.carrierName)}
                            {renderInfoField("Address", `${carrier?.carrierAddress}, ${carrier?.city}, ${carrier?.stateProvince} ${carrier?.zipPostalCode}, ${carrier?.country}`)}
                            {renderInfoField("Phone", carrier?.phoneNumber)}
                            {renderInfoField("Email", carrier?.carrierEmail)}
                        </Card>

                        <Card title="Registration & ID" hideButtons={true} mix="registration-id-card">
                            {renderInfoField(
                                <>
                                    SCAC Code{" "}
                                    <Popover
                                        values={
                                            <div className="popover-value">
                                                SCAC (Standard Carrier Alpha Code) — a unique four-letter code assigned to carriers by NMFTA for identification in shipping and freight systems.
                                            </div>
                                        }
                                    />
                                </>,
                                carrier?.scacCode
                            )}
                            {renderInfoField(
                                <>
                                    Motor Carrier (MC) No.{" "}
                                    <Popover
                                        values={
                                            <div className="popover-value">
                                                MC Number — a registration number issued by the FMCSA that allows carriers to operate as a for-hire transport company in the U.S.
                                            </div>
                                        }
                                    />
                                </>,
                                carrier?.mcNumber
                            )}
                        </Card>

                        <Card title="Insurance Expiration Dates" hideButtons={true} mix="insurance-expiration-dates">
                            {renderInfoField(<>
                                General Liability{" "}
                                <Popover
                                    values={
                                        <div className="popover-value">
                                            General Liability — covers third-party property or injury claims.
                                        </div>
                                    }
                                />
                            </>,
                                `Expires ${carrier?.generalLiability?.expiryDate} (${carrier?.generalLiability?.daysRemaining} days)`, carrier?.generalLiability?.status)}
                            {renderInfoField(<>
                                Auto Liability{" "}
                                <Popover
                                    values={
                                        <div className="popover-value">
                                            Auto Liability — covers damages caused by carrier vehicles.
                                        </div>
                                    }
                                />
                            </>, `Expires ${carrier?.autoLiability?.expiryDate} (${carrier?.autoLiability?.daysRemaining} days)`, carrier?.autoLiability?.status)}
                            {renderInfoField(<>
                                Motor Cargo{" "}
                                <Popover
                                    values={
                                        <div className="popover-value">
                                            Motor Cargo — protects goods being transported.
                                        </div>
                                    }
                                />
                            </>, `Expires ${carrier?.motorCargo?.expiryDate} (${carrier?.motorCargo?.daysRemaining} days)`, carrier?.motorCargo?.status)}
                            {renderInfoField(<>
                                Workman Comp{" "}
                                <Popover
                                    values={
                                        <div className="popover-value">
                                            Workman’s Comp — covers employee injury during work.
                                        </div>
                                    }
                                />
                            </>, `Expires ${carrier?.workmanComp?.expiryDate} (${carrier?.workmanComp?.daysRemaining} days)`, carrier?.workmanComp?.status)}
                        </Card>

                        <Card title="Notes" hideButtons={true}>
                            {renderSimpleCardContent(carrier?.notes || "No notes available.")}
                        </Card>

                        {UserUtils.isXGSAdministrator(userState.profile) && (
                            <div className="delete-button-container">
                                <Button className="delete-button" onClick={handleOpenDeleteConfirmation} theme={ButtonThemes.red}>Delete</Button>
                            </div>
                        )}
                    </div>
                )}
            </SlideOutSidebar>
            <ConfirmationModal
                opened={showDeleteConfirmation}
                onCancel={() => setShowDeleteConfirmation(false)}
                onConfirm={handleConfirmDelete}
            >
                <div className="confirmation-modal__content">
                    <AlertIcon />
                    <div>
                        Are you sure you want to delete the carrier:<br />
                        **{carrier?.carrierName}** (ID: {carrier?.carrierNumber})?
                    </div>
                </div>
            </ConfirmationModal>
        </>
    );
};

export default CarrierViewDetails;